import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Database, Users, Eye, Clock, Bell, Phone, Mail, MapPin, FileText, Scale } from 'lucide-react';

const PrivacyPolicyPageNew = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'intro',
      title: '1. Introduction',
      icon: FileText,
      content: (
        <div className="space-y-3">
          <p>Mothers Gold &amp; Diamonds LLP (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) operates the Mint a Milli platform. This Privacy Policy explains how we collect, use, and protect your personal information.</p>
        </div>
      )
    },
    {
      id: 'info-collect',
      title: '2. Information We Collect',
      icon: Database,
      content: (
        <div className="space-y-3">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-[#004D40]">Phone Number:</strong> Required for account creation and OTP verification.</li>
            <li><strong className="text-[#004D40]">Name:</strong> Required for KYC and voucher generation.</li>
            <li><strong className="text-[#004D40]">Email:</strong> Optional, used for communication and receipts.</li>
            <li><strong className="text-[#004D40]">Aadhaar Details:</strong> Your Aadhaar number and, when you verify using the Aadhaar QR code, the name, date of birth, gender and address contained in it. Required for KYC verification.</li>
            <li><strong className="text-[#004D40]">Address:</strong> Required for KYC verification.</li>
            <li><strong className="text-[#004D40]">PAN Details:</strong> Your PAN number and a photo of your PAN card, required once your total bookings reach ₹2,00,000.</li>
            <li><strong className="text-[#004D40]">Nominee Details:</strong> Optional. Your nominee&apos;s name, relationship and, if provided, their Aadhaar number.</li>
            <li><strong className="text-[#004D40]">Bank Details:</strong> Required for processing refunds (see Section 3).</li>
            <li><strong className="text-[#004D40]">Transaction History:</strong> Records of all bookings, refunds, redemptions and referral rewards.</li>
            <li><strong className="text-[#004D40]">Device &amp; App Information:</strong> App version and a push notification token, used to deliver notifications and keep the app up to date.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'bank',
      title: '3. Bank Account Information (DPDP Compliance)',
      icon: Lock,
      content: (
        <div className="space-y-3">
          <p>In compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act), we collect:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-[#004D40]">Bank Account Number:</strong> Stored in our access-controlled database. Only the last 4 digits are displayed in the app.</li>
            <li><strong className="text-[#004D40]">IFSC Code:</strong> Used to identify your bank branch.</li>
            <li><strong className="text-[#004D40]">Bank Name:</strong> For display and verification purposes.</li>
            <li><strong className="text-[#004D40]">Purpose Limitation:</strong> Your bank details are used exclusively for processing refunds. We do not share this information with third parties except as required to complete the transaction.</li>
            <li><strong className="text-[#004D40]">Consent:</strong> Bank details are only collected after you provide explicit consent. You can withdraw this consent at any time by deleting your bank details from your profile.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'usage',
      title: '4. How We Use Your Information',
      icon: Eye,
      content: (
        <div className="space-y-3">
          <ul className="list-disc pl-5 space-y-2">
            <li>To provide and maintain our services.</li>
            <li>To process transactions and send receipts.</li>
            <li>To verify your identity (KYC compliance) and ensure each Aadhaar is linked to only one account.</li>
            <li>To send notifications about your transactions, rates and offers.</li>
            <li>To improve our platform based on usage patterns.</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'security',
      title: '5. Data Security',
      icon: Shield,
      content: (
        <div className="space-y-3">
          <p>We protect your data with the following measures:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-[#004D40]">Encryption in Transit:</strong> All data is transmitted using TLS/HTTPS.</li>
            <li><strong className="text-[#004D40]">PAN Protection:</strong> Your PAN number is stored encrypted, and duplicate checks use a one-way hash.</li>
            <li><strong className="text-[#004D40]">Access-Controlled Storage:</strong> Aadhaar numbers, bank account numbers and other personal details are stored in our secured database, accessible only to authorised systems and staff.</li>
            <li><strong className="text-[#004D40]">PII Masking:</strong> Aadhaar, PAN and bank account numbers are masked in the app, showing only the last 4 digits.</li>
            <li><strong className="text-[#004D40]">Access Controls:</strong> Admin tools use role-based access, and staff access to PAN card images is logged.</li>
            <li><strong className="text-[#004D40]">Payments:</strong> UPI payments are processed by HDFC Bank. We never see or store your UPI PIN or card details.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'sharing',
      title: '6. Data Sharing',
      icon: Users,
      content: (
        <div className="space-y-3">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>HDFC Bank, for UPI payment processing.</li>
            <li>VEUP, our SMS provider, for OTP delivery.</li>
            <li>Google Firebase, for delivering push notifications.</li>
            <li>Government authorities, if required by law.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rights',
      title: '7. Your Rights (DPDP Act Compliance)',
      icon: Scale,
      content: (
        <div className="space-y-3">
          <p>Under the Digital Personal Data Protection Act, 2023, you have the following rights:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-[#004D40]">Right to Access:</strong> View all personal data we hold about you.</li>
            <li><strong className="text-[#004D40]">Right to Correction:</strong> Update inaccurate or incomplete information.</li>
            <li><strong className="text-[#004D40]">Right to Erasure:</strong> Request deletion of your data (e.g., bank details) when no longer needed, subject to legal retention requirements.</li>
            <li><strong className="text-[#004D40]">Right to Withdraw Consent:</strong> Revoke consent for data processing at any time.</li>
            <li><strong className="text-[#004D40]">Right to Grievance Redressal:</strong> Lodge complaints about data handling.</li>
            <li><strong className="text-[#004D40]">Right to Nominate:</strong> Designate a nominee to exercise your rights.</li>
          </ul>
          <p>To exercise any of these rights, contact us at support@mothersgold.in, or delete your bank details or account from your profile in the app.</p>
        </div>
      )
    },
    {
      id: 'cookies',
      title: '8. Cookies &amp; Local Storage',
      icon: Database,
      content: (
        <div className="space-y-3">
          <p>On the website we use cookies and your browser&apos;s local storage to keep you signed in and improve your experience. You can clear these in your browser settings, which will sign you out.</p>
        </div>
      )
    },
    {
      id: 'retention',
      title: '9. Data Retention &amp; Account Deletion',
      icon: Clock,
      content: (
        <div className="space-y-3">
          <p>We retain your data as follows:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-[#004D40]">Account Data:</strong> Retained while your account is active.</li>
            <li><strong className="text-[#004D40]">Bank Details:</strong> Retained until you delete them from your profile.</li>
            <li><strong className="text-[#004D40]">Transaction Records:</strong> Retained for 8 years as per Indian financial regulations.</li>
            <li><strong className="text-[#004D40]">KYC Records:</strong> Aadhaar, PAN, address and nominee details are retained as required by applicable Indian law.</li>
            <li><strong className="text-[#004D40]">On Account Deletion:</strong> Your name, email and phone number are removed from your profile immediately, and you are signed out of all devices and stop receiving notifications. KYC records, bank details and transaction records are retained as described above to meet legal and regulatory requirements. To remove your bank details, delete them from your profile before deleting your account.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'changes',
      title: '10. Changes to This Policy',
      icon: Bell,
      content: (
        <div className="space-y-3">
          <p>We may update this Privacy Policy periodically. Significant changes will be communicated via the app.</p>
        </div>
      )
    },
    {
      id: 'contact',
      title: '11. Contact Us',
      icon: Phone,
      content: (
        <div className="space-y-2">
          <p>For privacy-related queries:</p>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#B8860B]" />
            <span>support@mothersgold.in</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#B8860B]" />
            <span>+91 8137 830 916</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#B8860B]" />
            <span>Mothers Gold &amp; Diamonds LLP, Palakkad, Kerala, India</span>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen pb-8" data-testid="privacy-page-new">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004D40] to-[#00302B] px-4 py-4 sticky top-16 z-10 mx-2 rounded-lg shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-xl text-white font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
              Privacy Policy
            </h1>
            <p className="text-xs text-white/70">Last updated: November 2026</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Intro Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/30 p-6 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#004D40] to-[#00302B] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
                Your Privacy Matters
              </h2>
              <p className="text-sm text-[#B8860B]">Mothers Gold & Diamonds LLP</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            We are committed to protecting your personal information and being transparent about how we use it. 
            This policy explains our data practices in compliance with the Digital Personal Data Protection Act, 2023.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div 
              key={section.id}
              className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/20 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 p-4 border-b border-[#B8860B]/10 bg-gradient-to-r from-[#F5E6C8]/50 to-transparent">
                <div className="w-8 h-8 rounded-lg bg-[#004D40]/10 flex items-center justify-center">
                  <section.icon className="w-4 h-4 text-[#004D40]" />
                </div>
                <h3 className="text-base font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
                  {section.title}
                </h3>
              </div>
              <div className="p-4 text-sm text-gray-700 leading-relaxed">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#B8860B]/70">
            © 2026 Mothers Gold & Diamonds LLP. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPageNew;
